import type { ReactElement } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Navigate } from "react-router";
import { ColoredLabel, formatDateTimeFromISO } from "widgets";

import { getEventTagIntent, getTagIntentColor } from "@/app-utils/events.ts";
import { useURLViewID } from "@/app-utils/hooks/useUrl.ts";
import { getIconElementForPlaybook } from "@/app-utils/playbook.ts";
import type { InputTableQuerySpec } from "@/graphql.ts";
import { getPlaybookEventName, type IEventState } from "@/redux/modules/event.js";
import type { IAppState } from "@/redux/reducer.js";
import { DATATABLE_ID_TYPE, ENTITY_TYPE } from "@/settings/settings.ts";
import GenericDataTableContainer from "@/ui-modules/core/grid/GenericDataTableContainer.tsx";
import { AttributesListWidget } from "@/ui-modules/core/navigation-panel/AttributeWidget.tsx";
import { ChildEntitiesButtonHeader } from "@/ui-modules/core/navigation-panel/ButtonHeaderHoc.tsx";

const EventRelatedSamplesView = () => {
  const viewId = useURLViewID();
  const { event } = useSelector<IAppState, IEventState>((state) => state.event, shallowEqual);

  if (!event) {
    // should never happen as we will get an error page.
    return null;
  }

  // if this is a job-related event, proceed to the job summary page right away
  if (event.job) {
    return <Navigate to={`/job/${event.job.id}`} replace />;
  }

  const tableQuerySpec = {
    id: event.id,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: ENTITY_TYPE.EVENT,
    resultType: ENTITY_TYPE.SAMPLE,
  } satisfies InputTableQuerySpec;

  const btnMenu: ReactElement = (
    <ChildEntitiesButtonHeader
      tableSpec={tableQuerySpec}
      getParentEntityName={() => getPlaybookEventName(event)}
      hideAnalyzeBtn={true}
    />
  );

  return (
    <GenericDataTableContainer
      tableSpec={tableQuerySpec}
      project={event.project ?? undefined}
      rightToolbarElement={btnMenu}
      viewId={viewId ?? undefined}
      headerTags={[
        <img
          src={getIconElementForPlaybook(event?.playbook).large}
          width={53}
          height={30}
          key={"event-summary-view-tag-1"}
          alt={"playbook icon"}
        />,
      ]}
      headerAttributes={[
        <AttributesListWidget
          attributes={[
            { label: "Date", description: event ? formatDateTimeFromISO(event.created) : null },
            { label: "Playbook", description: event?.playbook.name },
            {
              label: "Status",
              description: (
                <ColoredLabel color={getTagIntentColor(getEventTagIntent(event?.status))} label={event?.status} />
              ),
            },
          ]}
          key={"event-summary-view-widget-1"}
        />,
      ]}
      title={getPlaybookEventName(event)}
      subtitle={"Samples"}
      noRowsText={"There are no related samples available for this event"}
    />
  );
};

export default EventRelatedSamplesView;
